import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PRICING } from '@/constants/testIds';
import { Sparkles, Check, BadgeCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PLAN_META = {
  basic: {
    tagline: 'Start your verified journey',
    color: 'from-cyan-500 to-blue-700',
    features: [
      'Verified profile listing',
      'Listed in artist directory',
      'Receive booking inquiries',
      'Standard search ranking',
    ],
    free: true,
    testIds: { card: PRICING.planBasic, button: PRICING.subscribeBasic },
  },
  professional: {
    tagline: 'For working artists',
    color: 'from-fuchsia-500 to-purple-700',
    features: [
      'Spotlight Pro verified badge',
      'Featured spotlight rotation',
      'Boosted AI search ranking',
      'Priority response to inquiries',
      'Analytics on profile views',
    ],
    highlighted: true,
    testIds: { card: PRICING.planProfessional, button: PRICING.subscribeProfessional },
  },
  elite: {
    tagline: 'Top 1% creators',
    color: 'from-amber-400 to-orange-500',
    features: [
      'Elite gold-verified badge',
      'Permanent homepage spotlight',
      'Top-of-search guaranteed',
      'Dedicated AI Talent Manager',
      'White-glove booking support',
      'Personal manager outreach',
    ],
    testIds: { card: PRICING.planElite, button: PRICING.subscribeElite },
  },
};

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState('');

  useEffect(() => {
    api.get('/payments/plans').then(({ data }) => setPlans(data.plans));
  }, []);

  const subscribe = async (planId) => {
    if (!user) { toast.error('Please sign in first'); navigate('/login'); return; }
    setLoading(planId);
    try {
      const { data } = await api.post('/payments/premium/checkout', {
        plan_id: planId, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error('Could not start checkout', { description: err?.response?.data?.detail });
      setLoading('');
    }
  };

  return (
    <div className="pt-28 pb-24 max-w-7xl mx-auto px-6 sm:px-12">
      <div className="text-center mb-16">
        <div className="text-xs uppercase tracking-[0.3em] text-amber-300 mb-3">Premium</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-4">
          Shine across the <span className="rainbow-text">universe</span>
        </h1>
        <p className="text-slate-400 max-w-xl mx-auto">Boost your visibility, unlock premium badges and dedicated AI assistance.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const meta = PLAN_META[plan.id] || PLAN_META.basic;
          const highlighted = meta.highlighted;
          return (
            <div
              key={plan.id}
              data-testid={meta.testIds.card}
              className={`relative rounded-3xl p-8 border ${highlighted ? 'border-fuchsia-400/40 glass-strong' : 'border-white/10 glass'} card-glow flex flex-col overflow-hidden`}
            >
              {highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 btn-rainbow text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full">
                  Most popular
                </div>
              )}
              <div className={`absolute -right-12 -top-12 h-40 w-40 rounded-full bg-gradient-to-br ${meta.color} opacity-25 blur-3xl`} />
              <div className="relative">
                <div className={`inline-flex h-11 w-11 rounded-xl bg-gradient-to-br ${meta.color} items-center justify-center mb-4`}>
                  <BadgeCheck className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-display text-2xl text-white font-bold">{plan.name}</h3>
                <p className="text-slate-400 text-sm mt-1 mb-6">{meta.tagline}</p>
                <div className="flex items-end gap-1 mb-6">
                  {plan.amount > 0 ? (
                    <>
                      <span className="font-display text-5xl font-bold text-white">₹{plan.amount.toLocaleString('en-IN')}</span>
                      <span className="text-slate-500 text-sm pb-2">/ month</span>
                    </>
                  ) : (
                    <span className="font-display text-5xl font-bold text-white">Free</span>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {meta.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-200">
                      <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                {meta.free ? (
                  <div
                    data-testid={meta.testIds.button}
                    className="w-full rounded-full py-3 text-center text-emerald-300 font-medium glass border border-emerald-400/30"
                  >
                    Included by default
                  </div>
                ) : (
                  <button
                    data-testid={meta.testIds.button}
                    onClick={() => subscribe(plan.id)}
                    disabled={!!loading}
                    className={`w-full rounded-full py-3 text-white font-medium ${highlighted ? 'btn-rainbow' : 'btn-ai'} disabled:opacity-60`}
                  >
                    {loading === plan.id ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</span> : <span className="inline-flex items-center gap-2 justify-center"><Sparkles className="h-4 w-4" /> Subscribe</span>}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-slate-500 mt-10">
        Powered by Stripe. Test mode — cards aren't charged. Use 4242 4242 4242 4242 with any future date / CVC.
      </p>
    </div>
  );
};

export default Pricing;
